// AccessCatalogsCard — Home "Access Catalogs" section.
// design ref: home.jsx AccessCatalogsCard. Header is the column variant (title +
// count + View all, with a "Request access to apps and resources" subtitle),
// then a 3-up grid. Per the user's request the cards use the coverflow
// CatalogCard (ported from ploy/client), not the bundle's flat `.catalog-card`.

import { Box, SimpleGrid } from "@chakra-ui/react";
import { TbPackage } from "react-icons/tb";

import Card from "components/ds/Card";
import CardHeader from "components/ds/CardHeader";
import EmptyState from "components/ds/EmptyState";
import type { MFAccessCatalog } from "state/members/access_v2/catalogs";
import CatalogCard from "./CatalogCard";

// design ref: home.jsx slices to 6 on the home dashboard; the full list lives
// behind "View all".
const MAX_CATALOGS = 6;

export interface AccessCatalogsCardProps {
  catalogs: MFAccessCatalog[];
  onViewAll: () => void;
  onOpenCatalog: (catalog: MFAccessCatalog) => void;
}

const AccessCatalogsCard = ({
  catalogs,
  onViewAll,
  onOpenCatalog,
}: AccessCatalogsCardProps) => (
  <Card>
    <CardHeader
      title="Access Catalogs"
      count={catalogs.length}
      action={{ label: "View all", onClick: onViewAll }}
      subtitle="Request access to apps and resources"
    />
    {catalogs.length === 0 ? (
      <EmptyState
        icon={<TbPackage />}
        title="No catalogs available yet"
        body="Your IT team sets up catalogs for your role. Once they're ready, you'll be able to browse and request access here."
      />
    ) : (
      // Container-relative (auto-fit), NOT viewport breakpoints: this card sits
      // in the ~half-width right column, so viewport `columns` would force 3
      // wide columns into a narrow container and overflow. minChildWidth wraps
      // based on the actual available width.
      <SimpleGrid minChildWidth="220px" spacing="md">
        {catalogs.slice(0, MAX_CATALOGS).map((catalog, i) => (
          <Box key={catalog.id} minW={0}>
            <CatalogCard
              catalog={catalog}
              onView={() => onOpenCatalog(catalog)}
              animateIn
              animationDelay={i * 80}
            />
          </Box>
        ))}
      </SimpleGrid>
    )}
  </Card>
);

export default AccessCatalogsCard;
